import React, { useState, useEffect } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';
import { getProducts, groupProductsByCategory } from '../services/api';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const Products = () => {
  const [productCategories, setProductCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      const data = await getProducts();
      const grouped = groupProductsByCategory(data);
      setProductCategories(grouped);
      setLoading(false);
    };
    fetchProducts();
  }, []);

  if (loading) {
    return (
      <section id="produtos" className="py-24 bg-gradient-to-b from-[#0a0a0a] to-[#0d0d0d] relative flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-[#E5B1C4] animate-spin" />
      </section>
    );
  }

  if (productCategories.length === 0) {
    return null;
  }

  return (
    <section id="produtos" className="py-24 bg-gradient-to-b from-[#0a0a0a] to-[#0d0d0d] relative">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(193,165,79,0.05)_0%,_transparent_50%)]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#E5B1C4] text-sm tracking-[0.3em] uppercase mb-4 block">
            Produtos Premium
          </span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl text-rose-gradient mb-6">
            Mary Kay
          </h2>
          <div className="flex items-center justify-center mb-6">
            <div className="w-24 h-px bg-gradient-to-r from-transparent to-[#E5B1C4]" />
            <span className="mx-4 text-[#E5B1C4]">✦</span>
            <div className="w-24 h-px bg-gradient-to-l from-transparent to-[#E5B1C4]" />
          </div>
          <p className="text-[#bbb5ae] max-w-2xl mx-auto text-lg">
            Consultora oficial Mary Kay - Descubra produtos de beleza de alta qualidade
          </p>
        </div>

        {/* Products Tabs */}
        <Tabs defaultValue={productCategories[0]?.category} className="w-full">
          <TabsList className="flex flex-wrap justify-center gap-2 mb-12 bg-transparent">
            {productCategories.map((cat, index) => (
              <TabsTrigger
                key={index}
                value={cat.category}
                className="px-6 py-3 rounded-none border border-[#E5B1C4]/20 text-[#f5f0e8] text-sm tracking-wide
                         data-[state=active]:bg-[#E5B1C4]/10 data-[state=active]:border-[#E5B1C4] data-[state=active]:text-[#E5B1C4]
                         hover:border-[#E5B1C4]/50 transition-all"
              >
                {cat.category}
              </TabsTrigger>
            ))}
          </TabsList>

          {productCategories.map((category, catIndex) => (
            <TabsContent key={catIndex} value={category.category}>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.products.map((product, index) => (
                  <div
                    key={product.id || index}
                    className="group bg-[#111111] border border-[#E5B1C4]/10 rounded-sm overflow-hidden
                             hover:border-[#E5B1C4]/30 transition-all duration-300
                             hover:shadow-lg hover:shadow-[#E5B1C4]/5"
                  >
                    {/* Product Image */}
                    {product.image && (
                      <div className="relative h-48 overflow-hidden bg-[#1a1a1a]">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        {/* Overlay gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-transparent opacity-60" />
                      </div>
                    )}

                    {/* Product Info */}
                    <div className="p-6">
                      <h4 className="font-display text-xl text-[#f5f0e8] mb-2 group-hover:text-[#E5B1C4] transition-colors">
                        {product.name}
                      </h4>
                      <p className="text-[#bbb5ae] text-sm mb-4">
                        {product.description}
                      </p>

                      {/* Price & CTA */}
                      <div className="flex items-center justify-between pt-4 border-t border-[#E5B1C4]/10">
                        <span className="text-[#E5B1C4] font-medium">
                          {product.price}
                        </span>
                        <button className="text-[#f5f0e8] hover:text-[#E5B1C4] text-sm flex items-center transition-colors group/btn">
                          Saber mais
                          <ChevronRight size={14} className="ml-1 group-hover/btn:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-[#bbb5ae] mb-6">
            Interessada em conhecer mais produtos ou fazer uma encomenda?
          </p>
          <Button
            onClick={() => document.querySelector('#contactos')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-gradient-to-r from-[#E5B1C4] to-[#FADCD9] text-[#0a0a0a] px-10 py-4 rounded-none
                     font-medium tracking-widest text-sm hover:opacity-90 transition-opacity"
          >
            CONTACTAR PARA ENCOMENDAS
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Products;
